"use client";

import { useEffect } from "react";
import { useFetch } from "@/lib/use-fetch";
import { Select } from "@/components/ui";
import type { SourceAddr } from "@/lib/types";

// Lists the sender IDs (sourceAddrs) assigned to the logged-in account. The gateway sends from the
// authenticated account, so these are the only valid "from" values. Backed by the shared cache
// (see useFetch) so it loads instantly on every page after the first — and it's warmed on sign-in.
export function SenderSelect({
  value,
  onChange,
  required
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  const { data, error } = useFetch<{ sourceAddrs: SourceAddr[] }>("auth/source-addrs", { staleMs: 60000 });
  const senders = data?.sourceAddrs ?? null;

  // Auto-select when the account has exactly one sender ID (and nothing chosen yet).
  useEffect(() => {
    if (senders && senders.length === 1 && !value) onChange(senders[0].sourceAddr);
  }, [senders, value, onChange]);

  if (error) return <p className="text-sm text-red-600">Could not load sender IDs: {error}</p>;

  return (
    <div>
      <Select label="Sender ID (from)" value={value} onChange={(e) => onChange(e.target.value)} required={required}>
        <option value="" disabled>
          {senders ? (senders.length ? "Select a sender…" : "No sender IDs assigned") : "Loading…"}
        </option>
        {senders?.map((s) => (
          <option key={s.sourceAddr} value={s.sourceAddr}>
            {s.sourceAddr}
          </option>
        ))}
      </Select>
      {senders && senders.length === 0 && (
        <p className="mt-1 text-xs text-amber-600">
          Your admin account has no sender IDs. Add one to your account before sending.
        </p>
      )}
    </div>
  );
}
